import React, { useEffect, useRef, useState } from "react";
import { Container, Card, CardBody, Badge, Button, Spinner, Input } from "reactstrap";
import { useParams } from "react-router-dom";
import {
  getPropertyTicketById, addPropertyTicketMessage, onPropertyTicketMessages,
} from "../../helpers/firestoreService";

const SEVERITY_COLORS = { Low: "info", Medium: "warning", High: "orange", Urgent: "danger" };
const STATUS_COLORS = { Open: "primary", "In Progress": "warning", Resolved: "success", Closed: "secondary" };

/**
 * Public, no-login view of a single ticket — the ticket's own (unguessable)
 * document id doubles as the shareable link. Anyone with this URL can read
 * the thread and reply (e.g. sharing with a contractor with no account).
 */
const PublicTicketPage = () => {
  document.title = "Ticket | Porcher";

  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [messages, setMessages] = useState([]);
  const [name, setName] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await getPropertyTicketById(ticketId);
        if (!t) { setNotFound(true); return; }
        setTicket(t);
      } catch (e) {
        console.error("Load ticket error:", e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) return;
    const unsub = onPropertyTicketMessages(ticketId, setMessages);
    return () => unsub();
  }, [ticketId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await addPropertyTicketMessage(ticketId, {
        senderName: name.trim() || "Guest",
        text: newMessage.trim(),
      });
      setNewMessage("");
    } catch (e) {
      console.error("Send message error:", e);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <Spinner color="primary" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center py-5 text-muted">
          <i className="bx bx-error-circle" style={{ fontSize: "2.5rem" }} />
          <p className="mt-2">This ticket link is invalid or no longer exists.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      <Container className="py-5" style={{ maxWidth: 720 }}>
        <Card className="border-0 shadow-sm mb-3">
          <CardBody>
            <h5 className="mb-1">{ticket.title}</h5>
            <div className="d-flex gap-2 mb-2">
              <Badge color={SEVERITY_COLORS[ticket.severity] || "secondary"} pill>{ticket.severity || "Low"}</Badge>
              <Badge color={STATUS_COLORS[ticket.status] || "secondary"} pill>{ticket.status}</Badge>
            </div>
            <p className="mb-0">{ticket.description}</p>
          </CardBody>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardBody>
            <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
              <i className="bx bx-chat me-1" />Conversation
            </h6>
            <div style={{ maxHeight: 360, overflowY: "auto" }} className="mb-3">
              {messages.length === 0 ? (
                <p className="text-muted small fst-italic mb-0">No messages yet.</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className="mb-2">
                    <div className="p-2 rounded bg-light" style={{ maxWidth: "85%" }}>
                      <div className="small fw-semibold mb-1">{m.senderName || "Anonymous"}</div>
                      <div className="small">{m.text}</div>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
            <form onSubmit={handleSend}>
              <div className="d-flex gap-2 mb-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  style={{ maxWidth: 220 }}
                  disabled={sending}
                />
              </div>
              <div className="d-flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message…"
                  disabled={sending}
                />
                <Button color="primary" type="submit" disabled={sending || !newMessage.trim()}>
                  {sending ? <Spinner size="sm" /> : <i className="bx bx-send" />}
                </Button>
              </div>
            </form>
            <small className="text-muted d-block mt-2" style={{ fontSize: "0.7rem" }}>
              By using this chat, you agree to our{" "}
              <a href="/community-guidelines" target="_blank" rel="noopener noreferrer">
                Community Guidelines
              </a>.
            </small>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default PublicTicketPage;
